import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { X, Loader2, CheckCircle2, XCircle, Sparkles, Star, Wand2 } from 'lucide-react';

const BulkOperationStatus = ({ token }) => {
  const [operations, setOperations] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  const fetchOperationStatus = useCallback(async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/ai/bulk-operations/status`,
        {
          headers: {
            'Authorization': token
          }
        }
      );
      
      const data = await response.json();
      
      if (data.success && data.operations) {
        setOperations(data.operations);
        
        // Show panel if there are processing operations
        const hasProcessing = data.operations.some(op => op.status === 'processing');
        if (hasProcessing) {
          setIsVisible(true);
        }
      }
    } catch (error) {
      console.error('Failed to fetch operation status:', error);
    }
  }, [token]);

  useEffect(() => {
    // Poll for operation status every 3 seconds
    const pollInterval = setInterval(fetchOperationStatus, 3000);
    
    // Initial fetch
    fetchOperationStatus();
    
    return () => clearInterval(pollInterval);
  }, [fetchOperationStatus]);

  const getOperationIcon = (type) => {
    switch (type) {
      case 'enrich':
        return <Sparkles className="w-4 h-4" />;
      case 'reviews':
        return <Star className="w-4 h-4" />;
      case 'metadata_fix':
        return <Wand2 className="w-4 h-4" />;
      default:
        return <Loader2 className="w-4 h-4" />;
    }
  };

  const getOperationLabel = (type) => {
    switch (type) {
      case 'enrich':
        return 'AI Enrich All';
      case 'reviews':
        return 'Generate Reviews';
      case 'metadata_fix':
        return 'Fix Metadata';
      default:
        return 'Operation';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'processing':
        return <Loader2 className="w-4 h-4 animate-spin text-blue-500" />;
      case 'completed':
        return <CheckCircle2 className="w-4 h-4 text-green-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  const dismissOperation = (operationId) => {
    setOperations(operations.filter(op => op.id !== operationId));
    
    // Hide panel if no more operations
    if (operations.length <= 1) {
      setIsVisible(false);
    }
  };

  if (!isVisible || operations.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed bottom-4 right-4 z-50 w-96 max-w-[calc(100vw-2rem)]"
      data-testid="bulk-operation-status-panel"
    >
      <Card className="shadow-lg border-2">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-950/20 dark:to-purple-950/20">
          <div className="flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
            <h3 className="font-semibold text-sm">Background Operations</h3>
            <span className="text-xs bg-blue-100 dark:bg-blue-900 px-2 py-0.5 rounded-full">
              {operations.filter(op => op.status === 'processing').length} active
            </span>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsMinimized(!isMinimized)}
              className="h-6 w-6 p-0"
              title={isMinimized ? "Expand" : "Minimize"}
            >
              {isMinimized ? '+' : '−'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsVisible(false)}
              className="h-6 w-6 p-0"
              title="Hide panel"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Operations List */}
        {!isMinimized && (
          <div className="max-h-96 overflow-y-auto">
            {operations.map((operation) => (
              <div
                key={operation.id}
                className="p-4 border-b last:border-b-0 hover:bg-muted/50 transition-colors"
                data-testid={`operation-${operation.id}`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getOperationIcon(operation.type)}
                    <span className="font-medium text-sm">
                      {getOperationLabel(operation.type)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusIcon(operation.status)}
                    {operation.status === 'completed' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => dismissOperation(operation.id)}
                        className="h-6 w-6 p-0"
                        title="Dismiss"
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                {operation.status === 'processing' && (
                  <>
                    <Progress value={operation.progress || 0} className="h-2 mb-2" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>
                        {operation.completed_items || 0} / {operation.total_items} items
                      </span>
                      <span>{operation.progress || 0}%</span>
                    </div>
                  </>
                )}

                {/* Completion Info */}
                {operation.status === 'completed' && (
                  <div className="text-xs text-green-600 dark:text-green-400">
                    ✓ Completed {operation.completed_items} items
                  </div>
                )}

                {/* Error Info */}
                {operation.status === 'failed' && operation.error && (
                  <div className="text-xs text-red-600 dark:text-red-400">
                    ✗ Failed: {operation.error}
                  </div>
                )}

                {/* Time Info */}
                <div className="text-xs text-muted-foreground mt-1">
                  Started: {new Date(operation.started_at).toLocaleTimeString()}
                  {operation.completed_at && (
                    <> • Completed: {new Date(operation.completed_at).toLocaleTimeString()}</>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default BulkOperationStatus;
